<template>
  <span class="main-wrapper" @mouseenter="showTooltip" @mouseleave="hideTooltip" @click="clickTitle">
    <span class="info-icon">{{ label }}</span>
    <InfoTooltipContent
      v-if="isTooltipVisible"
      :id="id"
      :title="title"
      :page="page"
      :snippet="snippet"
      :thumb-url="thumbUrl"
      @title-clicked="clickTitle"
      class="tooltip-container"
      :style="tooltipStyle"
    />
  </span>
</template>

<script>
import { computed, ref } from 'vue';

import { decodeFromBase64 } from '~/utils/message';
import InfoTooltipContent from '~/components/conversation/chatMessage/InfoTooltipContent.vue';

export default {
  name: 'InfoTooltip',
  components: { InfoTooltipContent },
  emits: ['titleClicked'],
  props: {
    id: {
      type: String,
      default: '',
    },
    snippetIndex: {
      type: String,
      default: '',
    },
    messageData: {
      type: Object,
      default: () => {},
    },
    page: {
      type: String,
      default: '',
    },
    label: {
      type: Number,
      default: 1,
    },
    onGetTooltipThumb: {
      type: Function,
      default: () => {},
    },
    tooltipStyle: {
      type: Object,
      default: () => {},
    },
  },

  setup(props, { emit }) {
    const isTooltipVisible = ref(false);
    const thumbUrl = ref('');
    const currentReference = ref({});

    const currentIndex = computed(
      () => (props.messageData?.data?.[0]?.references || []).findIndex((ref) => ref.externalId === props.id) + 1,
    );

    const title = computed(() => currentReference.value?.name || '');

    const snippet = computed(() => {
      if (!currentReference.value?.snippets) {
        return '';
      }
      const specificSnippet = currentReference.value.snippets[`${props.page}-${props.snippetIndex}`];
      return '...' + decodeFromBase64(specificSnippet || currentReference.value?.snippet || '');
    });

    const loadPageCover = async () => {
      thumbUrl.value = await props.onGetTooltipThumb(props.id, props.page);
    };

    const showTooltip = async () => {
      currentReference.value = (props.messageData?.data?.[0]?.references || []).find(
        (ref) => ref.externalId === props.id,
      );
      isTooltipVisible.value = true;
      await loadPageCover();
    };

    const hideTooltip = () => (isTooltipVisible.value = false);
    const clickTitle = () => emit('titleClicked', { reference: currentReference.value, page: Number(props.page) });

    return {
      currentIndex,
      currentReference,
      clickTitle,
      thumbUrl,
      isTooltipVisible,
      showTooltip,
      hideTooltip,
      title,
      snippet,
    };
  },
};
</script>

<style scoped lang="scss">
.main-wrapper {
  position: relative;

  .info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 16px;
    height: 16px;
    background-color: $bb-neutral-1;
    border-radius: 50%;
    color: $bb-neutral-0;
    box-sizing: border-box;
    margin-right: $bb-spacing-1;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;

    &:hover {
      background-color: $bb-primary-3;
    }
  }

  .tooltip-container {
    position: absolute;
    left: 50%;
    transform: translateX(-10%);
    background-color: $bb-neutral-0;
    width: 300px; /* Adjust width */
    max-height: 232px;
    overflow: hidden;
    border: 1px solid $bb-neutral-3;
    border-radius: 4px;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column; /* Stack rows */
    z-index: 1000;
  }
}
</style>
